import { PropsWithChildren } from "react"
import { useFormikContext } from "formik"
import { Box, Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSparkles } from "@fortawesome/pro-solid-svg-icons"
import { makeStyles } from "tss-react/mui"

import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { LocalizedEnum, LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { FormikSwitch } from "../../../../../../shared/components/forms/Formik"
import { StandardSegmentedControl } from "../../../../../../shared/components/segmentedControl/StandardSegmentedControl"

import { isTemplateValueRequired } from "../helpers"
import { DishFieldConfig, DishFormValues, DishParameterKey, DishParameterType } from "../menuTemplatesTypes"

// Prefilled is not offered as a mode; saved Prefilled parameters load as Locked (see toDishParameterType).
const MODE_OPTIONS = [DishParameterType.LOCKED, DishParameterType.MANDATORY, DishParameterType.OPTIONAL]

// Each field's value path, used as the card's DOM id so StandardFormikErrorScrollWrapper can
// scroll to cards whose inputs have no name attribute (weekday chips, allergens, image).
const FIELD_VALUE_PATHS: Record<DishParameterKey, string> = {
    [DishParameterKey.TITLE]: "value",
    [DishParameterKey.PRICE]: "value",
    [DishParameterKey.DESCRIPTION]: "value",
    [DishParameterKey.ALLERGENS]: "value",
    [DishParameterKey.AVAILABILITY]: "weekDays",
    [DishParameterKey.MARKETING_TAG]: "value",
    [DishParameterKey.IMAGE]: "photo",
    [DishParameterKey.INGREDIENTS]: "value",
    [DishParameterKey.NUTRITION]: "value",
    [DishParameterKey.CO2_FOOTPRINT]: "value",
}

const useStyles = makeStyles()((theme) => ({
    card: {
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
        borderRadius: ".5rem",
        background: theme.palette.background.paper,
    },
    fieldName: {
        fontWeight: 700,
    },
    hiddenHint: {
        color: theme.palette.tailwind.slate[400],
    },
    cardError: {
        borderColor: theme.palette.error.main,
    },
    head: {
        padding: ".5rem 1rem",
    },
    toggle: {
        flexShrink: 0,
        "& .MuiFormControl-root": {
            width: "auto",
        },
    },
    body: {
        padding: "1rem",
        borderTop: `1px solid ${theme.palette.tailwind.slate[100]}`,
    },
    valueArea: {
        width: "100%",
        paddingTop: "1rem",
        borderTop: `1px dashed ${theme.palette.tailwind.slate[200]}`,
    },
    valueLabel: {
        color: theme.palette.tailwind.slate[500],
    },
    hint: {
        color: theme.palette.tailwind.slate[500],
    },
    aiRow: {
        width: "100%",
        paddingTop: "1rem",
        borderTop: `1px dashed ${theme.palette.tailwind.slate[200]}`,
    },
    aiLabel: {
        fontWeight: 700,
        color: theme.palette.tailwind.purple[700],
    },
    aiLabelOff: {
        color: theme.palette.tailwind.slate[400],
    },
    aiIcon: {
        fontSize: "1rem",
        marginRight: ".375rem",
    },
    aiHint: {
        maxWidth: "22.5rem",
        color: theme.palette.tailwind.slate[400],
    },
}))

type Props = Readonly<
    PropsWithChildren<{
        fieldKey: DishParameterKey
        namePrefix: string
        config: DishFieldConfig
        aiFill?: boolean
        hasError?: boolean
    }>
>

export function DishFieldCard({ fieldKey, namePrefix, config, aiFill, hasError = false, children }: Props) {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()
    const { setFieldValue } = useFormikContext<DishFormValues>()

    // A data array, so the labels come from getString rather than a LocalizedEnum wrapper.
    const modeOptions = MODE_OPTIONS.map((type) => ({
        value: type,
        label: l10n.getStringForEnum("merchant-menu-templates-mode", type, type),
    }))

    const valueLabelId = isTemplateValueRequired(config.type)
        ? "merchant-menu-templates-field-value-locked-label"
        : "merchant-menu-templates-field-value-default-label"

    return (
        <Stack
            id={`${namePrefix}.${FIELD_VALUE_PATHS[fieldKey]}`}
            className={cx(classes.card, hasError && classes.cardError)}
        >
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing="1rem"
                className={classes.head}
            >
                <Stack direction="row" alignItems="center" spacing=".75rem" flexGrow={1}>
                    <LocalizedEnum base="merchant-menu-templates-field" enumValue={fieldKey}>
                        <Typography variant="subtitle1" className={classes.fieldName}>
                            {fieldKey}
                        </Typography>
                    </LocalizedEnum>
                    {!config.isShown && (
                        <LocalizedStrict id="merchant-menu-templates-field-hidden-hint">
                            <Typography variant="caption" className={classes.hiddenHint}>
                                Hidden in week menu
                            </Typography>
                        </LocalizedStrict>
                    )}
                </Stack>
                <Box className={classes.toggle}>
                    <LocalizedStrict id="merchant-menu-templates-field-show-toggle">
                        <FormikSwitch name={`${namePrefix}.isShown`} color="success">
                            Show
                        </FormikSwitch>
                    </LocalizedStrict>
                </Box>
            </Stack>

            {config.isShown && (
                <Stack alignItems="flex-start" spacing="1rem" className={classes.body}>
                    <StandardSegmentedControl
                        value={config.type}
                        onChange={(type) => setFieldValue(`${namePrefix}.type`, type)}
                        options={modeOptions}
                    />
                    <Stack alignItems="flex-start" spacing=".5rem" className={classes.valueArea}>
                        <LocalizedStrict id={valueLabelId}>
                            <Typography variant="overline" className={classes.valueLabel}>
                                Default value
                            </Typography>
                        </LocalizedStrict>
                        {children}
                    </Stack>
                    <LocalizedEnum base="merchant-menu-templates-mode-hint" enumValue={config.type}>
                        <Typography variant="body2" className={classes.hint}>
                            {config.type}
                        </Typography>
                    </LocalizedEnum>

                    {aiFill !== undefined && (
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            spacing="1rem"
                            className={classes.aiRow}
                        >
                            <Stack>
                                <Typography
                                    variant="body2"
                                    className={cx(classes.aiLabel, !aiFill && classes.aiLabelOff)}
                                >
                                    <FontAwesomeIcon icon={faSparkles} className={classes.aiIcon} />
                                    <LocalizedStrict id="merchant-menu-templates-ai-can-fill-label">
                                        <span>AI can fill this field</span>
                                    </LocalizedStrict>
                                </Typography>
                                <LocalizedStrict
                                    id={
                                        aiFill
                                            ? "merchant-menu-templates-ai-hint-on"
                                            : "merchant-menu-templates-ai-hint-off"
                                    }
                                >
                                    <Typography variant="caption" className={classes.aiHint}>
                                        AI generation in the week menu may fill this field.
                                    </Typography>
                                </LocalizedStrict>
                            </Stack>
                            <Box className={classes.toggle}>
                                <FormikSwitch name={`${namePrefix}.aiFill`} color="secondary" />
                            </Box>
                        </Stack>
                    )}
                </Stack>
            )}
        </Stack>
    )
}
